import { useMemo, useState } from 'react';
import { Pagination } from '../../../components/shared';
import { useI18n } from '../../../i18n';
import { cn } from '../../../utils/cn';

export interface LowStockItem {
  id: string;
  productName: string;
  imageUrl?: string;
  stock: number;
  stockUnit: string;
  /**
   * Tồn còn lại tính theo % của mức tồn tối đa (kho đầy = 100%).
   * null khi BE không có `max_quantity` — hiện "—" chứ không bịa số.
   */
  percentLeft: number | null;
  status: 'critical' | 'warning' | 'low' | 'ok';
}

interface LowStockWarningTableProps {
  /** Chỉ những mặt hàng thực sự còn ít (hết hàng / thủng ngưỡng / dưới ngưỡng %). */
  items: LowStockItem[];
  isLoading?: boolean;
  onReorder?: () => void;
  pageSize?: number;
  className?: string;
}

/** Tô màu theo mức độ cạn: càng ít càng đỏ. */
function percentTone(percent: number | null): string {
  if (percent === null) return 'text-slate-400';
  if (percent <= 10) return 'text-[#E8472A]';
  if (percent <= 20) return 'text-[#D08700]';
  return 'text-[#0284C7]';
}

const statusConfig = {
  critical: { label: 'Out of stock', color: 'text-[#E8472A]', bg: 'bg-[#FFF0ED]', border: 'border-[#FFCFC7]' },
  warning:  { label: 'Below min',    color: 'text-[#D08700]', bg: 'bg-[#FFFBEB]', border: 'border-[#FFE9A0]' },
  low:      { label: 'Running low',  color: 'text-[#0284C7]', bg: 'bg-[#F0F9FF]', border: 'border-[#BAE6FD]' },
  ok:       { label: 'In stock',     color: 'text-[#1FBE63]', bg: 'bg-[#F0FDF4]', border: 'border-[#C0F9D4]' },
};

export function LowStockWarningTable({
  items,
  isLoading = false,
  onReorder,
  pageSize = 5,
  className,
}: LowStockWarningTableProps) {
  const { t } = useI18n();
  const [currentPage, setCurrentPage] = useState(1);

  const rows = items;
  const totalPages = Math.max(Math.ceil(rows.length / pageSize), 1);
  // Dữ liệu đổi (đổi store / đổi khoảng ngày) có thể làm trang hiện tại vượt range.
  const safePage = Math.min(currentPage, totalPages);
  const paginated = useMemo(
    () => rows.slice((safePage - 1) * pageSize, safePage * pageSize),
    [rows, safePage, pageSize]
  );

  if (isLoading) {
    return <div className={cn('h-[300px] animate-pulse rounded-xl bg-slate-200', className)} />;
  }

  return (
    <div
      className={cn('rounded-xl bg-white shadow-sm', className)}
      style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-6 py-4">
        <div>
          <h3 className="font-sans text-base font-bold text-slate-900">{t('lowProductWarning')}</h3>
          {/* Nói rõ tiêu chí vào bảng, để không ai phải đoán vì sao một sản phẩm
              có mặt ở đây. */}
          <p className="mt-0.5 text-xs text-slate-400">{t('lowProductWarningSubtitle')}</p>
        </div>
        {rows.length === 0 && (
          <span className="shrink-0 rounded-md border border-[#C0F9D4] bg-[#F0FDF4] px-2.5 py-0.5 text-xs font-semibold text-[#1FBE63]">
            {t('allAboveThreshold')}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-y border-slate-100 bg-slate-50/60">
              <th className="whitespace-nowrap px-6 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400">{t('dashboardProduct')}</th>
              {/* Nhãn phải tự giải thích được: "STOCK"/"MININUM" (sai chính tả)
                  rồi "REORDER AT" đều không nói rõ con số bên dưới nghĩa là gì. */}
              <th
                className="whitespace-nowrap px-6 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400"
                title={t('quantityLeftHint')}
              >
                {t('quantityLeft')}
              </th>
              <th
                className="whitespace-nowrap px-6 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400"
                title={t('percentLeftHint')}
              >
                {t('percentLeft')}
              </th>
              <th className="whitespace-nowrap px-6 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400">{t('dashboardStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                {/* Chỉ rỗng thật khi kho chưa có dữ liệu tồn nào cả. */}
                <td colSpan={4} className="px-6 py-10 text-center">
                  <p className="text-sm font-medium text-slate-600">{t('noLowStockProducts')}</p>
                  <p className="mt-1 text-xs text-slate-400">{t('noLowStockProductsHint')}</p>
                </td>
              </tr>
            ) : (
              paginated.map((item) => {
                const cfg = statusConfig[item.status];
                return (
                  <tr key={item.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.productName} className="h-10 w-10 rounded-lg object-cover" />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
                            <span className="text-xs font-medium">{(item.productName ?? '?').charAt(0)}</span>
                          </div>
                        )}
                        <span className="text-sm font-medium text-slate-900">{item.productName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <span className="text-sm text-slate-700">{item.stock} {item.stockUnit}</span>
                    </td>
                    <td className="px-6 py-3">
                      {item.percentLeft === null ? (
                        <span className="text-sm text-slate-400" title={t('percentLeftUnknown')}>—</span>
                      ) : (
                        <span className={cn('text-sm font-semibold', percentTone(item.percentLeft))}>
                          {item.percentLeft < 1 && item.percentLeft > 0
                            ? '<1%'
                            : `${Math.round(item.percentLeft)}%`}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className={cn(
                          'inline-block rounded-md border px-2.5 py-0.5 text-xs font-semibold',
                          cfg.color, cfg.bg, cfg.border
                        )}
                      >
                        {cfg.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {paginated.length > 0 && (
        <Pagination
          variant="numbered"
          currentPage={safePage}
          totalPages={totalPages}
          totalItems={rows.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          itemLabel={t('products')}
          className="border-t border-slate-100"
        />
      )}
    </div>
  );
}
